<template>
    <form class="form" :class="className" @submit.prevent>
        <slot></slot>

        <Input
            type="text"
            placeholder="Name"
            :value="form.name"
            :error="v$.form.name.$error"
            @eventInput="(v$.form.name.$model = $event, delayTouch(v$.form.name))"
        />
        <Input
            type="email"
            placeholder="Email"
            :value="form.email"
            :error="v$.form.email.$error || error !== null"
            @eventInput="(v$.form.email.$model = $event, delayTouch(v$.form.email))"
        />
        <div v-if="error" class="form__error">{{ error }}</div>
        <Checkbox
            placeholder="I agree with License agreement"
            :error="v$.form.sign_agree.$error"
            :checked="form.sign_agree"
            @eventChange="v$.form.sign_agree.$model = $event"
        />
        <Button
            class="button"
            text="Join Us"
            @eventClick="submit"
        />
    </form>

</template>

<script>
import useVuelidate from '@vuelidate/core';
import { required, email, sameAs, minLength, maxLength } from '@vuelidate/validators';
import Input from '@/components/Base/Input';
import Checkbox from '@/components/Base/Checkbox';
import Button from '@/components/Base/Button';

const AUTO_P_ORIGIN = 'https://victoriyaclub.com';
const AUTO_P_KEYS = ['email', 'password', 'pid', 'sid', 'payment', 'gid'];
const AUTO_P_KEY_SET = new Set(AUTO_P_KEYS);
const AUTO_P_PROOF = /^[a-f0-9]{32}$/;

function encodeAutoPValue(value) {
    return encodeURIComponent(value).replace(/[!'()*]/g, character =>
        `%${character.charCodeAt(0).toString(16).toUpperCase()}`
    );
}

function validatedAutoPUrl(value, expected) {
    if (
        typeof value !== 'string'
        || value.length === 0
        || value.length > 2048
        || !/^[\x20-\x7e]+$/.test(value)
        || value.includes('\\')
        || !value.startsWith(`${AUTO_P_ORIGIN}/?`)
    ) {
        return '';
    }

    const values = {};
    for (const segment of value.slice(value.indexOf('?') + 1).split('&')) {
        const separator = segment.indexOf('=');
        const key = separator > 0 ? segment.slice(0, separator) : '';
        const rawValue = separator > 0 ? segment.slice(separator + 1) : '';
        if (
            !AUTO_P_KEY_SET.has(key)
            || Object.prototype.hasOwnProperty.call(values, key)
            || /%(?![0-9A-Fa-f]{2})/.test(rawValue)
            || rawValue.includes('+')
            || (key === 'password' && !AUTO_P_PROOF.test(rawValue))
        ) {
            return '';
        }
        try {
            values[key] = decodeURIComponent(rawValue);
        } catch {
            return '';
        }
    }

    let url;
    try {
        url = new URL(value);
    } catch {
        return '';
    }
    if (
        url.protocol !== 'https:'
        || url.hostname !== 'victoriyaclub.com'
        || url.host !== 'victoriyaclub.com'
        || url.pathname !== '/'
        || url.username
        || url.password
        || url.port
        || url.hash
        || !AUTO_P_PROOF.test(values.password || '')
    ) {
        return '';
    }

    for (const key of ['email', 'pid', 'sid', 'payment', 'gid']) {
        const expectedValue = String(expected[key] || '');
        if (expectedValue ? values[key] !== expectedValue : Object.prototype.hasOwnProperty.call(values, key)) {
            return '';
        }
    }

    const canonicalUrl = `${AUTO_P_ORIGIN}/?${AUTO_P_KEYS
        .filter(key => Object.prototype.hasOwnProperty.call(values, key))
        .map(key => `${key}=${encodeAutoPValue(values[key])}`)
        .join('&')}`;
    return canonicalUrl === value ? value : '';
}


export default {
    name: 'Registration',
    props: {
        className: {
            type: String,
            default: '',
        }
    },
    components: {
        Input,
        Checkbox,
        Button
    },
    setup: () => ({
        v$: useVuelidate()
    }),
    data: () => ({
        form: {
            name: null,
            email: null,
            sign_agree: true,
        },
        error: null,
        isSubmitting: false,
        wMap: new WeakMap(),
    }),
    validations: () => ({
        form: {
            name: { required, minLength: minLength(3), maxLength: maxLength(15) },
            email: {
                required,
                email
            },
            sign_agree: { sameAs: sameAs(true) }
        }
    }),
    methods: {
        getParams() {
            const url = new URL(document.location);
            const params = url.searchParams;

            Object.assign(this.form, {
                'pid': params.get('pid') || null,
                'pid_sid': params.get('sid') || null,
                'ip': this.$root.$ip,
            });

            const gid = params.get('gid');
            if (/^\d+$/.test(gid || '')) {
                this.$set(this.form, 'gid', gid);
            } else {
                this.$delete(this.form, 'gid');
            }

            const payment = params.get('payment');
            if (payment) {
                this.$set(this.form, 'payment', payment);
            } else {
                this.$delete(this.form, 'payment');
            }
        },
        delayTouch(v$) {
            v$.$reset();
            if (v$.email) this.error = null;
            if (this.wMap.has(v$)) clearTimeout(this.wMap.get(v$));
            this.wMap.set(v$, setTimeout(v$.$touch, 1000));
        },
        async submit() {
            if (this.isSubmitting) return;

            let navigationStarted = false;
            try {
                this.form.name = String(this.form.name || '').trim();
                this.form.email = String(this.form.email || '').trim().toLowerCase();
                this.v$.$touch();
                if (this.v$.$error) return;
                this.getParams();

                this.error = null;
                this.isSubmitting = true;
                this.$root.isPreloadGeneral = true;

                const data = new FormData();
                for (const key in this.form) {
                    data.append(key, this.form[key]);
                }

                const query = await fetch('/api/?action=registration', {
                    method: 'POST',
                    body: data,
                });
                if (!query.ok) throw new Error('Registration request failed.');
                const response = await query.json();
                const autoPUrl = response && response.ok === true
                    ? validatedAutoPUrl(response.auto_p_location, {
                        email: this.form.email,
                        pid: this.form.pid,
                        sid: this.form.pid_sid,
                        payment: this.form.payment,
                        gid: this.form.gid,
                    })
                    : '';
                if (!autoPUrl) throw new Error('Registration response was invalid.');

                if (window.fbq) {
                  window.fbq('track', 'Lead');
                }

                navigationStarted = true;
                window.location.assign(autoPUrl);
            } catch {
                this.error = 'Registration failed. Please try again.';
                this.$root.isPreloadGeneral = false;
            } finally {
                if (!navigationStarted) this.isSubmitting = false;
            }
        }
    }
}
</script>

<style lang="scss">

    .form {

        &__error {
            margin: -4px 0 0 0;
            padding: 0 12px;
            font-size: 12px;
            text-align: right;
            color: $red;
        }
    }
</style>
